import { ComponentRef, TemplateRef, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DetailDrawerFormData } from '@app/components/popup/types/drawer-form.types';
import { Observable } from '@app/shared/rx';
import { PermissionService } from '@app/shared/services';
import {
  GridsterItemComponentInterface,
  IFormAction,
  IFormGroupPreferences,
  IFormRules,
  GridsterItem,
  SchemaNode,
} from '@app/shared/types';
import { PredicateFn } from '@app/shared/types/common-extension.types';
import { DndMode } from '../enums';
import { XuxDndItemComponent } from '../xux-dnd-item/xux-dnd-item.component';
import { Subject } from 'rxjs';
import { I18n } from '@app/i18n/strings-keys';

export interface DndConfig {
  modeObs?: Observable<DndMode>;
  /**
   * custom dynamic widgets
   */
  widgetsMap?: { [widgetName: string]: Type<any> };

  /**
   * pass this as true to cache form value, and apply when form init
   */
  cacheFormValue?: boolean;
}

export interface DndItemContent {
  schema?: SchemaNode;
  formRules: IFormRules;

  autoRefresh?: {
    enabled: boolean;
    interval: number; // in seconds
    autoRefreshObs: Subject<boolean>;
  };
  /**
   * pass this as true to cache form value, and apply when form init
   * this will use the same property in the DndConfig as default
   */
  cacheFormValue?: boolean;

  validation?: boolean;

  defaultWidgetFamily?: string;

  showLoader?: boolean;

  onFormAction?: Function;

  data?: DetailDrawerFormData | PredicateFn<Promise<DetailDrawerFormData>>;

  on?: {
    formReady?: (form: FormGroup, groups: IFormGroupPreferences[]) => void;
    formValueChanged?: (data: DetailDrawerFormData) => void;
    remove?: (item: DndItem) => void;
  };
}

export interface DndItemStyle {
  maxHeight?: string;
  height?: string;
  width?: string;
}

export interface DndItem {
  key: DashboardModuleKey;
  title?: string;
  content: DndItemContent;
  style?: DndItemStyle;
  fixed?: boolean;
  hidden?: boolean;

  /**
   * use this to judge is can render this module
   * priority is over than hidden
   */
  permissionChecker?: (permissionService: PermissionService) => boolean;
}

export interface IGridItemConfig {
  y?: number;
  x?: number;
  cols: number;
  rows: number;
  key: DashboardModuleKey;
  itemConfig?: DndItem;
  template?: TemplateRef<any>;
  class?: string;
  initCallback?: (
    item: GridsterItem,
    itemComponent: GridsterItemComponentInterface,
  ) => void;
}

export type IItemToPush = {
  [key in DashboardModuleKey]: GridsterItemComponentInterface;
};

export type IModule = {
  [key in DashboardModuleKey]: DndItem;
};

export interface DndColListConfig {
  key: string;
  title?: string;
  items: DndItem[];
  order?: number;
}

export interface DndListConfig {
  [rowKey: string]: Omit<DndColListConfig, 'key'>;
}

export interface FormReadyEvent {
  form: FormGroup;
  groups: IFormGroupPreferences[];
}

export interface ItemFormReadyEvent {
  itemKey: string;
  event: FormReadyEvent;
}

export interface ItemFormAction {
  itemKey: string;
  action: IFormAction;
}

export interface CreateComponentEvent {
  componentCreated?: (compRef: ComponentRef<XuxDndItemComponent>) => void;
  componentsCreatedDone?: () => void;
}

export const enum DashboardModuleKey {
  Event = 'events',
  InventoryValue = 'inventory_value',
  PurchaseOrders = 'purchase_orders',
  Transfers = 'transfers',
  StandWorksheet = 'stand_worksheet',
}

export const DashboardModuleKeyNameMapping = {
  [DashboardModuleKey.Event]: I18n.EntityNames.Events,
  [DashboardModuleKey.InventoryValue]: I18n.EntityNames.InventoryValue,
  [DashboardModuleKey.PurchaseOrders]:
    I18n.Components.LandingPageComponent.PurchaseOrders,
  [DashboardModuleKey.Transfers]: I18n.EntityNames.Transfers,
  [DashboardModuleKey.StandWorksheet]: I18n.EntityNames.StandWorksheets,
};
